import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type React from 'react';

import MuxPlayer from '@mux/mux-player-react';
import type { MuxPlayerRefAttributes } from '@mux/mux-player-react';

import { cn } from '@change/ds-utils';

import { useI18n } from '@change-corgi/core-react/i18n';
import { ClientRender } from '@change-corgi/core-react/ssr/render';

import { isMotionReduced } from '#src/app/shared/utils/accessibility';
import { isMuxPlayerSupported, parseSourceInfoFromUrl } from '#src/app/shared/utils/petitionVideo';

import { MONTAGE_VIDEO_URL } from '../../montage';

/** Mean luminance (0-255) above which the video is judged to be showing something, not black. */
const NOT_BLACK_LUMINANCE = 12;

/** Longest the still waits for a lit frame before revealing the player regardless. */
const FRAME_WAIT_TIMEOUT_MS = 5000;

/**
 * Whether the video is currently painting something other than black.
 *
 * Sampled from the pixels rather than inferred from an event. `playing` only means playback was
 * started, and even `requestVideoFrameCallback` reports frames that are decoded rather than lit -
 * both can lead the first visible frame by an unbounded margin on a loaded machine. The pixels
 * cannot be early: if they are bright, there is something to look at.
 *
 * Returns false if the canvas is tainted, which happens when the asset is served without CORS
 * headers. The caller falls back to the media events in that case.
 */
function isShowingLitFrame(video: HTMLVideoElement, canvas: HTMLCanvasElement): boolean {
	const ctx = canvas.getContext('2d', { willReadFrequently: true });
	if (!ctx || video.videoWidth === 0) return false;

	try {
		ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
		const { data } = ctx.getImageData(0, 0, canvas.width, canvas.height);
		let total = 0;
		for (let i = 0; i < data.length; i += 4) {
			total += 0.2126 * data[i] + 0.7152 * data[i + 1] + 0.0722 * data[i + 2];
		}
		return total / (data.length / 4) > NOT_BLACK_LUMINANCE;
	} catch {
		return false;
	}
}

type LitFrameWatcher = {
	isVideoLit: boolean;
	/** Attach as the player's `ref`; also usable as the fallback `onPlaying` handler. */
	watchForLitFrame: (node: MuxPlayerRefAttributes | null) => void;
	revealPlayer: () => void;
};

/**
 * `media.nativeEl` is Mux Player's own accessor for the underlying `<video>`, the same one
 * `PetitionVideoPlayer` uses. Reaching into the shadow DOM would work today and break silently the
 * next time Mux restructures it.
 */
function useLitFrameWatcher(): LitFrameWatcher {
	const [isVideoLit, setIsVideoLit] = useState(false);
	const cleanupRef = useRef<(() => void) | undefined>(undefined);

	const revealPlayer = useCallback(() => {
		setIsVideoLit(true);
	}, []);

	const watchForLitFrame = useCallback(
		(node: MuxPlayerRefAttributes | null) => {
			cleanupRef.current?.();
			cleanupRef.current = undefined;

			const video = node?.media?.nativeEl;
			if (!video) return;

			const canvas = document.createElement('canvas');
			canvas.width = 16;
			canvas.height = 18;

			let stopped = false;
			const poll = () => {
				if (stopped) return;
				if (isShowingLitFrame(video, canvas)) {
					revealPlayer();
					return;
				}
				window.requestAnimationFrame(poll);
			};
			window.requestAnimationFrame(poll);

			cleanupRef.current = () => {
				stopped = true;
			};
		},
		[revealPlayer],
	);

	useEffect(() => {
		// Backstop: a tainted canvas makes the sampler permanently blind, and reduced motion means no
		// frame is ever painted. Neither should be able to strand the still indefinitely.
		const timeout = setTimeout(revealPlayer, FRAME_WAIT_TIMEOUT_MS);
		return () => {
			clearTimeout(timeout);
			cleanupRef.current?.();
		};
	}, [revealPlayer]);

	return { isVideoLit, watchForLitFrame, revealPlayer };
}

/**
 * The looping supporter-video montage on the ask surface.
 *
 * The Mux thumbnail covers the player until the video is provably showing a lit frame. A `<video>`
 * paints opaque black until it has one - that is the element's own rendering rather than a
 * background colour, so nothing placed behind it can help, and `getComputedStyle` reports it
 * transparent the whole time. The still therefore sits on top.
 *
 * Swapped, not cross-faded: a fade spends its duration blending the still into whatever is behind
 * it, so a mistimed one displays the black rather than hiding it.
 *
 * The still doubles as the reduced-motion state: autoplay is suppressed there, no frame is ever
 * painted, and it simply stays - the "hold the montage on a static frame" behaviour, which CSS alone
 * cannot do to a video.
 */
export const MontageVideo: React.FC = () => {
	const { translate } = useI18n();
	const sourceInfo = useMemo(() => (MONTAGE_VIDEO_URL ? parseSourceInfoFromUrl(MONTAGE_VIDEO_URL) : undefined), []);
	const { isVideoLit, watchForLitFrame, revealPlayer } = useLitFrameWatcher();

	if (!sourceInfo) {
		return null;
	}

	const label = translate('corgi.petition_actions.video_first_ask.montage_label');

	return (
		<>
			<ClientRender>
				{() => {
					// Read at mount rather than subscribed to: the montage only needs to not start moving
					// under reduced motion, and a supporter changing the OS setting mid-screen is not a case
					// worth a listener for.
					const shouldAutoPlay = !isMotionReduced();

					/*
					 * `MuxPlayer` calls `new WeakRef()` with no polyfill path, so it throws on Chrome <84 and
					 * Safari <14.5 (CHANGE-93371). Falling through to the plain `<video>` gives those browsers
					 * the montage over HLS, which they will not play - so they get the poster still, which is
					 * the same quiet degradation as having no montage configured.
					 */
					if (sourceInfo.type === 'mux' && isMuxPlayerSupported()) {
						return (
							<MuxPlayer
								ref={watchForLitFrame}
								playbackId={sourceInfo.muxPlaybackId}
								poster={sourceInfo.poster}
								streamType="on-demand"
								aria-label={label}
								data-testid="video-first-ask-montage"
								autoPlay={shouldAutoPlay}
								loop
								muted
								playsInline
								className="h-full w-full"
								style={{
									// Mux Player renders its own control bar and there is no native `controls`
									// attribute to withhold, so this CSS var is how Mux's docs say to hide it.
									'--controls': 'none',
									// The video lives in Mux Player's shadow DOM, so an `object-cover` class on the
									// host element never reaches it - this var is the supported way in. The montage
									// is a 0.05% ratio match for this slot, so it crops almost nothing; the cover
									// is here so an asset swapped in at a different ratio still fills the box
									// rather than letterboxing.
									'--media-object-fit': 'cover',
								}}
							/>
						);
					}

					return (
						<video
							src={sourceInfo.src}
							preload={sourceInfo.preload}
							aria-label={label}
							data-testid="video-first-ask-montage"
							autoPlay={shouldAutoPlay}
							loop
							muted
							playsInline
							onPlaying={revealPlayer}
							className="h-full w-full object-cover"
						/>
					);
				}}
			</ClientRender>

			{/*
			 * Last in the DOM and positioned, so it covers the player without needing a z-index. Server-
			 * rendered, so the preload scanner fetches it during parse rather than after hydration.
			 * Decorative: the player carries the accessible name, and announcing this too would repeat it.
			 */}
			{sourceInfo.type === 'mux' ? (
				<img
					src={sourceInfo.poster}
					alt=""
					aria-hidden
					data-testid="video-first-ask-montage-still"
					className={cn('pointer-events-none absolute inset-0 h-full w-full object-cover', {
						hidden: isVideoLit,
					})}
				/>
			) : null}
		</>
	);
};
